import { cn } from '@shared/components/shadcn';
import DOMPurify, { type UponSanitizeElementHook } from 'isomorphic-dompurify';

type ArticleContentProps = {
  content: string;
  className?: string;
  'data-testid'?: string;
};

const YOUTUBE_EMBED_SRC_PATTERN = /^https:\/\/www\.youtube(?:-nocookie)?\.com\/embed\//;

// Body content ships as plain `<iframe>` embeds (YouTube only, verified against live articles) and
// embedded ToC `<script type="application/ld+json">` blocks — `RichContent`'s DOMPurify profile
// forbids both outright, so this component defines its own rather than reusing `RichContent`. `id`
// (ToC anchor targets), FAQPage microdata (`itemscope`/`itemtype`/`itemprop`) and iframe/link
// attributes not in DOMPurify's default allow-list are added explicitly.
const JSON_LD_SCRIPT_TYPE = 'application/ld+json';

const SANITIZE_CONFIG = {
  ADD_TAGS: ['iframe', 'script'],
  ADD_ATTR: ['target', 'allow', 'allowfullscreen', 'frameborder', 'referrerpolicy', 'itemscope', 'itemtype', 'itemprop', 'type'],
  FORBID_TAGS: ['style', 'object', 'embed'],
  FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onmouseout', 'onkeydown', 'onkeyup', 'onkeypress'],
  // DOMPurify strips `<script>` unless content is parsed as a document body fragment.
  FORCE_BODY: true,
};

function changeTagName(el: HTMLElement, newTagName: string) {
  const newElement = el.ownerDocument!.createElement(newTagName);

  for (const attribute of el.attributes) {
    newElement.setAttribute(attribute.name, attribute.value);
  }

  for (const childNode of el.childNodes) {
    newElement.appendChild(childNode.cloneNode(true));
  }

  el.replaceWith(newElement);
}

const sanitizeArticleElements: UponSanitizeElementHook = (node, data) => {
  // Remove non-YouTube iframe embeds
  if (data.tagName === 'iframe') {
    const src = (node as Element).getAttribute('src');
    if (!src || !YOUTUBE_EMBED_SRC_PATTERN.test(src)) {
      node.parentNode?.removeChild(node);
    }
    return;
  }

  // Remove non-JSON-LD script blocks
  if (data.tagName === 'script') {
    const type = (node as Element).getAttribute('type');
    if (!type || type.toLowerCase() !== JSON_LD_SCRIPT_TYPE) {
      node.parentNode?.removeChild(node);
    }
  }

  // Convert div.faq-item to details/summary for accessibility
  if (data.tagName === 'div' && (node as HTMLElement).classList.contains('faq-item')) {
    const question = (node as HTMLElement).querySelector<HTMLHeadingElement>(':scope > h3:first-child');
    if (question) {
      changeTagName(question, 'summary');
      changeTagName(node as HTMLElement, 'details');
    }
  }
};

function sanitizeArticleContent(content: string): string {
  // The hook is added immediately before, and removed immediately after, this single synchronous
  // `sanitize()` call — it never spans an `await` — so it can't leak into other `DOMPurify.sanitize()`
  // calls elsewhere in the app (e.g. `RichContent`) that share the same `isomorphic-dompurify` singleton.
  DOMPurify.addHook('uponSanitizeElement', sanitizeArticleElements);
  try {
    return DOMPurify.sanitize(content, SANITIZE_CONFIG);
  } finally {
    DOMPurify.removeHook('uponSanitizeElement', sanitizeArticleElements);
  }
}

function ArticleContent({ content, className, ...props }: ArticleContentProps) {
  const sanitizedContent = sanitizeArticleContent(content);

  return <article className={cn('external-html article-content', className)} dangerouslySetInnerHTML={{ __html: sanitizedContent }} {...props} />;
}

export { ArticleContent, type ArticleContentProps };
